import { useEffect } from "react";
import { connect } from "react-redux";
import * as d3 from 'd3';
import store from "../store";
import { updateItem, updateScale } from "../canvasSlice";
import { getSVGPathData } from "../Util";
import { getPeersInScene } from "mascot-vis";

const getHandlePosition = {
    nw: (b) => ({x: b.left, y: b.top}),
    n: (b) => ({x: b.x, y: b.top}),
    ne: (b) => ({x: b.right, y: b.top}),
    e: (b) => ({x: b.right, y: b.y}),
    se: (b) => ({x: b.right, y: b.bottom}),
    s: (b) => ({x: b.x, y: b.bottom}),
    sw: (b) => ({x: b.left, y: b.bottom}),
    w: (b) => ({x: b.left, y: b.y})
}

let axisHandlePosition = undefined;
const showAxisHandle = (scene, axis) => {
    if (!axis) return;
    const h = d3.select("#axisHandle").style("visibility", "visible");
    h.select("rect").call(d3.drag()
        .on("start", function(e) {
            if (axis.channel === "x" || axis.channel === "width") {
                axisHandlePosition = axis.bounds.middle;
            } else if (axis.channel === "y" || axis.channel === "height") {
                axisHandlePosition = axis.bounds.center;
            }
        })
        .on("end", function(e) {
            axisHandlePosition = undefined;
        })
        .on("drag", function(e) {
            e.sourceEvent.stopPropagation();
            let dir = axis.isFlipped ? -1 : 1;
            const scale = axis.encoding.scale;
            if (axis.channel === "x" || axis.channel === "width") {
                let newRE = scale.rangeExtent + e.dx * dir;
                if (newRE <= 0) {
                    if (scale.getEncodedChannels().indexOf("width") < 0)
                        scale.isFlipped = !scale.isFlipped;
                    else
                        return;
                }
                axis.encoding.scale.rangeExtent = newRE;
                axis.autoUpdateTicks();
            } else if (axis.channel === "y" || axis.channel === "height") {
                let newRE = scale.rangeExtent - e.dy * dir;
                if (newRE <= 0) {
                    if (scale.getEncodedChannels().indexOf("height") < 0)
                        scale.isFlipped = !scale.isFlipped;
                    else
                        return;
                }
                axis.encoding.scale.rangeExtent = newRE;
                axis.autoUpdateTicks();
            }
            store.dispatch(updateScale({id: axis.encoding.scale.id, type: axis.encoding.scale.type, axis: axis.id}));
        }));
    updateAxisHandlePosition(axis);
}

const updateAxisHandlePosition = (axis) => {
    if (!axis) return;
    const h = d3.select("#axisHandle");
    let x1, y1, x2, y2, x, y, wd, ht, tx, ty;
    switch (axis.channel) {
        case "x":
        case "width":
            x1 = axis.bounds.left;
            y1 = axisHandlePosition ? axisHandlePosition : axis.bounds.middle;
            x2 = axis.bounds.right;
            y2 = y1;
            wd = 45;
            ht = 16;
            x = axis.isFlipped ? axis.bounds.left : axis.bounds.right - wd;
            y = y1 - ht/2;
            tx = x + 5;
            ty = y + ht/2;
            h.select("text").style("writing-mode", "lr")
            break;
        case "y":
        case "height":
            x1 = axisHandlePosition ? axisHandlePosition : axis.bounds.center;
            y1 = axis.bounds.top;
            x2 = x1;
            y2 = axis.bounds.bottom;
            wd = 16;
            ht = 45;
            x = x1 - wd/2;
            y = axis.isFlipped ? axis.bounds.bottom - ht : axis.bounds.top;
            tx = x1 + 3;
            ty = y + 5;
            h.select("text").style("writing-mode", "tb").style("glyph-orientation-vertical", 0);
            break;
        default:
            break;
    }
    h.select("line").attr("x1", x1).attr("x2", x2).attr("y1", y1).attr("y2", y2);
    h.select("rect").attr("x", x).attr("y", y).attr("width", wd).attr("height", ht).attr("rx", 6).attr("ry", 6);
    h.select("text").text(axis.encoding.scale.rangeExtent.toFixed(0) + "px").attr("x", tx).attr("y", ty);
}

const hideAxisHandle = () => {
    d3.select("#axisHandle").style("visibility", "hidden");
}

const redrawRect = (b, styles, rect) => {
    const attrs = {
        "x": b.left,
        "y": b.top, 
        "width": b.width,
        "height": b.height
    };
    for (let a in attrs)
        rect.attr(a, attrs[a]);
    for (let s in styles)
        rect.style(s, styles[s]);
};

const redrawSelection = (selected, scene) => {
    //clear selection first
    d3.select("#selectionUI").selectAll("circle").style("visibility", "hidden");
    d3.select("#handleG").style("visibility", "hidden");
    d3.selectAll(".openGroup").style("visibility", "hidden");
    d3.select("#pathOutline").style("visibility", "hidden");
    d3.select("#selectPeerOutline").style("visibility", "hidden");
    d3.select("#boundsOutline").style("visibility", "hidden");
    
    if (selected.length === 1) {
        const itm = scene.getItem(selected[0]);
        if (!itm) return;
        //draw peer outline
        let peers = getPeersInScene(itm);
        if (peers.length > 1) {
            let peerPathData = getSVGPathData(peers, 2);
            d3.select("#selectPeerOutline").style("visibility", "visible").attr("d", peerPathData);
        }

        //draw item outline
        let styles, b;
        if (itm.type === "collection" || itm.type === "axis" || itm.type === "legend") {
            styles = {"stroke": "#1ecb40", "stroke-dasharray": "5,5", "stroke-width": 2, "fill": "none"};
        } else {
            styles = {"stroke": "#1592e6", "stroke-dasharray": "none", "stroke-width": 1.5,  "fill": "none"};
        }
        if (itm.type === "legend") {
            b = itm.bounds.clone();
            b.left -= 10;
            b.top -= 10;
            b.width += 20;
            b.height += 20;
        } else {
            b = itm.bounds;
        }
        if (itm.type === "path" || itm.type === "area" || itm.type === "polygon" || itm.type === "arc" || itm.type === "pie") {
            d3.select("#boundsOutline").style("visibility", "hidden");
            d3.select("#pathOutline").style("visibility", "visible");
            d3.select("#pathSegments").attr("d", itm.getSVGPathData());
            d3.select("#pathVertices").attr("d", getSVGPathData(itm.vertices, 2));
        } else {
            d3.select("#boundsOutline").style("visibility", "visible");
            d3.select("#pathOutline").style("visibility", "hidden");
            //d3.select("#itemOutline").style("visibility", "visible");
            redrawRect(b, styles, d3.select("#boundsOutline"));
        }

        //draw parents
        d3.selectAll(".openGroup").style("visibility", "hidden");
        let parent = itm.parent, cnt = 1;
        const parentStyles = {"stroke": "#1ecb40", "stroke-width": 4, "fill": "none", "opacity": 0.15, "visibility": "visible"};
        while (parent && parent.type !== "scene") {
            redrawRect(parent.bounds, parentStyles, d3.select("#openGroup"+cnt));
            parent = parent.parent;
            cnt += 1;
        }

        //draw handles
        d3.select("#handleG").style("visibility", "visible");
        const g = d3.select("#selectionUI"), resizable = ["rect", "circle", "line", "pointText"];
        if (resizable.indexOf(itm.type) < 0) {
            g.selectAll("circle").style("visibility", "hidden");
            return;
        } 
        
        for (let h of handles) {
            let p = getHandlePosition[h](b);
            g.select("." + h).style("cursor", h+"-resize").attr("cx", p.x).attr("cy", p.y).style("visibility", "visible");
        }

        if (itm.type === "line") {
            if (itm.bounds.width === 0) {
                g.selectAll("circle").style("visibility", "hidden");
                g.select(".n").style("visibility", "visible");
                g.select(".s").style("visibility", "visible");
            }
            if (itm.bounds.height === 0) {
                g.selectAll("circle").style("visibility", "hidden");
                g.select(".e").style("visibility", "visible");
                g.select(".w").style("visibility", "visible");
            }
        } else if (itm.type === "pointText") {
            //todo: show se anchor
            g.selectAll("circle").style("visibility", "hidden");
            g.select(".se").style("visibility", "visible");
        } 
    } else if (selected.length > 1) {
        const itms = selected.map(d => scene.getItem(d));
        d3.select("#handleG").style("visibility", "visible");
        let styles = {"stroke": "#1592e6", "stroke-dasharray": "none", "stroke-width": 1.5,  "fill": "none", "visibility": "visible"};
        let bounds = itms[0].bounds.clone();
        for (let i = 1; i < itms.length; i++)
            bounds = bounds.union(itms[i].bounds);
        redrawRect(bounds, styles, d3.select("#boundsOutline"));
        d3.select("#selectPeerOutline").style("visibility", "hidden");
        d3.select("#pathOutline").style("visibility", "hidden");
    }
}

const redrawDirectSelection = (directSelected, scene) => {
    d3.selectAll(".directSelectPreview").style("visibility", "hidden");
    if (directSelected) {
        const itm = scene.getItem(directSelected);
        if (!itm) {
            d3.selectAll(".directSelected").style("visibility", "hidden");
            d3.select("#directSelectPeerOutline").style("visibility", "hidden");
            return;
        }
        //draw peer outline
        let peers = getPeersInScene(itm);
        if (peers.length > 1) {
            let peerPathData = getSVGPathData(peers, 4);
            d3.select("#directSelectPeerOutline").style("visibility", "visible").attr("d", peerPathData);
        }

        const styles = {"stroke": "#1592e6", "stroke-width": 2, "fill": "none", "visibility": "visible"};
        if (itm.type === "segment") {
            let seg = d3.select("#segment").attr("x1", itm.vertex1.x).attr("y1", itm.vertex1.y)
                        .attr("x2", itm.vertex2.x).attr("y2", itm.vertex2.y);
            for (let s in styles)
                seg.style(s, styles[s]);
            d3.select("#vertex").style("visibility", "hidden");
        } else {
            let b = {left: itm.x - 2, top: itm.y - 2, width: 4, height: 4};
            redrawRect(b, styles, d3.select("#vertex"));
            d3.select("#segment").style("visibility", "hidden");
        } 
    } else {
        d3.selectAll(".directSelected").style("visibility", "hidden");
        d3.select("#directSelectPeerOutline").style("visibility", "hidden");
    }
}

const redrawDirectSelectPreview = (directSelected, scene) => {
    d3.selectAll(".directSelectPreview").style("visibility", "hidden");
    if (directSelected) {
        const itm = scene.getItem(directSelected);
        if (!itm) return;
        const styles = {"stroke": "#79AFE0", "stroke-width": 2, "fill": "none", "visibility": "visible"};
        if (itm.type === "segment") {
            let seg = d3.select("#segmentPreview").attr("x1", itm.vertex1.x).attr("y1", itm.vertex1.y)
                        .attr("x2", itm.vertex2.x).attr("y2", itm.vertex2.y);
            for (let s in styles)
                seg.style(s, styles[s]);
        } else {
            let b = {left: itm.x - 3, top: itm.y - 3, width: 6, height: 6};
            redrawRect(b, styles, d3.select("#vertexPreview"));
        } 
    }
}

const redrawGridGaps = (selected, scene) => {
    if (selected.length === 1) {
        const itm = scene.getItem(selected[0]);
        if (itm && itm.type === "collection" && itm.layout && itm.layout.type === "grid") {
            const layout = itm.layout, cb = layout.cellBounds, numCols = layout.numCols, numRows = layout.numRows;
            let topCellTop = Math.min(...cb.map(d => d.top)), leftCellLeft = Math.min(...cb.map(d => d.left));
            let data = [];
            for (let i = 0; i < numCols - 1; i++) {
                // let x = layout.colGap < 0 ? cb[i].right + layout.colGap : cb[i].right;
                // data.push({x: x, y: cb[i].top, width: Math.abs(layout.colGap), height: itm.bounds.height, type: "c"});
                data.push({x: leftCellLeft + cb[0].width + i * (cb[0].width + layout.colGap), y: topCellTop, width: Math.abs(layout.colGap), height: itm.bounds.height, type: "c"});
            }
            for (let i = 0; i < numRows - 1; i++) {
                // let y = layout.rowGap < 0 ? cb[i * numCols].bottom + layout.rowGap : cb[i * numCols].bottom;
                // data.push({x: cb[0].left, y: y, width: itm.bounds.width, height: Math.abs(layout.rowGap), type: "r"});
                data.push({x: leftCellLeft, y: topCellTop + cb[0].height + i * (cb[0].height + layout.rowGap) , width: itm.bounds.width, height: Math.abs(layout.rowGap), type: "r"});
            }
            let rects = d3.select("#gridGaps").style("visibility", "visible").selectAll("rect").data(data);
            rects.enter().append("rect").merge(rects)
                .attr("x", d => d.x).attr("y", d => d.y)
                .attr("width", d => d.width).attr("height", d => d.height)
                .style("fill", "pink").style("opacity", 0.2)
                .style("cursor", d => d.type === "r" ? "ns-resize" : "ew-resize")
                .call(d3.drag().on("drag", function(e) {
                    e.sourceEvent.stopPropagation();
                    const type = d3.select(this).datum().type;
                    let o = {}, g = type === "r" ? "rowGap" : "colGap", d = type === "r" ? e.dy : e.dx;
                    o[g] = itm.layout[g] + d;
                    scene.setProperties(itm.layout, o);
                    store.dispatch(updateItem({id: selected[0], type: itm.type, properties: o}));
                }))
                ;
            rects.exit().remove();
        } else {
            d3.select("#gridGaps").style("visibility", "hidden");
        }
    } else {
        d3.select("#gridGaps").style("visibility", "hidden");
    }
}

var resizeX, resizeY;

const resizeStarted = (e) => {
    resizeX = e.x;
    resizeY = e.y;
};

const resizing = function(e, h, selected, scene) {
    let b = getResizeBounds(h, selected, e.x, e.y, scene);
    let g = d3.select("#handleG");
    d3.select("#boundsOutline").attr("x", b.left).attr("y", b.top).attr("width", b.width).attr("height", b.height);
    for (let h of handles) {
        let p = getHandlePosition[h](b);
        g.select("." + h).attr("cx", p.x).attr("cy", p.y);
    }
};

const findItemToSnap = function(scene, selected, o, pos) {
    let children = scene.children.filter(d => d !== selected && d.type !== "axis" && d.type !== "legend" && d.type !== "gridlines");
    let candidates = [], anchors = o === "x" ? ["left", "right", "center"] : ["top", "bottom", "middle"];
    for (let c of children) {
        for (let a of anchors) {
            candidates.push({item: c, anchor: a});
        }
    }
    candidates.sort((a,b) => Math.abs(a.item.bounds[a.anchor] - pos) - Math.abs(b.item.bounds[b.anchor] - pos));
    return candidates[0];
}

const resizeEnded = function(e, h, selected, scene) {
    let b = getResizeBounds(h, selected, e.x, e.y, scene);
    let toChange;
    if (selected.type === "rect" || selected.type === "line") {
        toChange = {width: b.width, height: b.height, xRef: (h.indexOf("e") < 0 ? "right" : "left"), yRef: (h.indexOf("n") < 0 ? "top" : "bottom")};
    } else if (selected.type === "circle") {
        toChange = {radius: Math.max(b.width, b.height)/2, xRef: (h.indexOf("e") < 0 ? "right" : "left"), yRef: (h.indexOf("n") < 0 ? "top" : "bottom")};
    } else if (selected.type === "pointText") {
        let fs = Math.floor(parseInt(selected.fontSize) * b.width/selected.bounds.width);
        fs = Math.max(4, fs);
        toChange = {"fontSize": fs+"px"}
    }

    if (toChange) {
        scene.setProperties(selected, toChange);
        store.dispatch(updateItem({id: selected.id, type: selected.type, properties: toChange}));
    }
};

const getResizeBounds = (h, selected, ex, ey, scene) => {
    let itmBounds = selected.bounds, b = {
        top: itmBounds.top,
        height: itmBounds.height,
        left: itmBounds.left,
        width: itmBounds.width
    };
   
    let t = 5, hc = (h !== "n" && h !== "s") ? findItemToSnap(scene, selected, "x", ex) : undefined, 
            vc = (h !== "e" && h !== "w") ? findItemToSnap(scene, selected, "y", ey) : undefined;
    let dx = ex - resizeX, dy = ey - resizeY;
    if (hc && Math.abs(hc.item.bounds[hc.anchor] - ex) <= t) {
        dx = hc.item.bounds[hc.anchor] - resizeX;
        d3.select("#hGuide").style("visibility", "visible").attr("x1", hc.item.bounds[hc.anchor]).attr("x2", hc.item.bounds[hc.anchor])
            .attr("y1", Math.min(hc.item.bounds.top, selected.bounds.top) - 10).attr("y2", Math.max(hc.item.bounds.bottom, selected.bounds.bottom) + 10);
    } else {
        d3.select("#hGuide").style("visibility", "hidden");
    }
    if (vc && Math.abs(vc.item.bounds[vc.anchor] - ey) <= t) {
        dy = vc.item.bounds[vc.anchor] - resizeY;
        d3.select("#vGuide").style("visibility", "visible").attr("y1", vc.item.bounds[vc.anchor]).attr("y2", vc.item.bounds[vc.anchor])
            .attr("x1", Math.min(vc.item.bounds.left, selected.bounds.left) - 10).attr("x2", Math.max(vc.item.bounds.right, selected.bounds.right) + 10);
    } else {
        d3.select("#vGuide").style("visibility", "hidden");
    }

    if (h.indexOf("s") >= 0) {
        if (dy < -itmBounds.height + 1)
            dy = -itmBounds.height + 1;
        b.height += dy;
    } else if (h.indexOf("n") >= 0) {
        if (dy > itmBounds.height - 1)
            dy = itmBounds.height - 1;
        b.height -= dy;
        b.top += dy;   
    }

    if (h.indexOf("e") >= 0) {
        if (dx < -itmBounds.width + 1)
            dx = -itmBounds.width + 1;
        b.width += dx;
    } else if (h.indexOf("w") >= 0) {
        if (dx > itmBounds.width - 1)
            dx = itmBounds.width - 1;
        b.left += dx;
        b.width -= dx;
    }

    b.right = b.left + b.width;
    b.bottom = b.top + b.height;
    b.x = (b.left + b.right)/2;
    b.y = (b.top + b.bottom)/2;

    return b;
}

const mapStateToProps = state => ({
    selected: state.canvas.selected,
    directSelected: state.canvas.directSelected,
    directSelectPreview: state.canvas.directSelectPreview,
    itemUpdated: state.canvas.itemUpdated,
    axisHandleVisible: state.canvas.axisHandleVisible,
    scaleUpdated: state.canvas.scaleUpdated,
    rectSelectRegion: state.canvas.rectSelectRegion
});

const handles = ["nw", "n", "ne", "w", "sw", "s", "se", "e"];

function SelectionUI(props) {

    useEffect(() => {
        if (props.selected.length === 0) return;
        let selected = props.scene.getItem(props.selected[0]);
        d3.select("#selectionUI").selectAll("circle").style("stroke", "#1592e6").style("fill", "white")
            .call(d3.drag().on("start", resizeStarted).on("drag", function(e) { resizing(e, d3.select(this).attr("class"), selected, props.scene) })
            .on("end", function(e) {
                resizeEnded(e, d3.select(this).attr("class"), selected, props.scene);
            }))
    }, [props.scene, props.selected]);

    useEffect(() => {
        redrawSelection(props.selected, props.scene);
        redrawDirectSelection(props.directSelected, props.scene);
        redrawGridGaps(props.selected, props.scene);
        d3.selectAll(".smartGuide").style("visibility", "hidden");
    }, [props.scene, props.selected, props.directSelected, props.itemUpdated, props.scaleUpdated]);

    useEffect(() => {
        redrawDirectSelectPreview(props.directSelectPreview, props.scene);
    }, [props.directSelectPreview, props.scene])

    useEffect(() => {
        if (props.axisHandleVisible.visible)
            showAxisHandle(props.scene, props.scene.getItem(props.axisHandleVisible.axis));
        else
            hideAxisHandle();
    }, [props.scene, props.axisHandleVisible]);

    useEffect(() => {
        if (props.rectSelectRegion.width === 0 || props.rectSelectRegion.height === 0)
            d3.select("#rectSelectRegion").style("visibility", "hidden");
        const styles = {"stroke": "#ccc", "stroke-width": 2, "fill": "none", "stroke-dasharray": "5,5", "opacity": 1, "visibility": "visible"};
        redrawRect(props.rectSelectRegion, styles, d3.select("#rectSelectRegion"));
    }, [props.rectSelectRegion]);

    useEffect(() => {
        if (props.scaleUpdated.axis)
            updateAxisHandlePosition(props.scene.getItem(props.scaleUpdated.axis));
    }, [props.scene, props.scaleUpdated]);

    return (
        <g id="selectionUI">
            <rect id="openGroup1" className="openGroup"></rect>
            <rect id="openGroup2" className="openGroup"></rect>
            <rect id="openGroup3" className="openGroup"></rect>
            <rect id="openGroup4" className="openGroup"></rect>
            <path id="selectPeerOutline" style={{"stroke": "#60bdf0", "strokeWidth": 1,  "fill": "none"}}></path>
            <path id="directSelectPeerOutline" style={{"stroke": "#60bdf0", "strokeWidth": 1,  "fill": "none"}}></path>
            <rect id="boundsOutline"></rect>
            <g id="handleG">
                {handles.map(h => <circle key={h} className={h} r="4"></circle>)}
            </g>
            <g id="axisHandle">
                <line style={{strokeWidth:"2px", stroke:"#1ecb40"}}></line>
                <rect style={{fill:"#1ecb40"}}></rect>
                <text style={{fill:"white", "fontSize": "11px", "pointerEvents": "none", fontWeight: "bold", "alignmentBaseline": "middle", "dominantBaseline": "middle"}}></text>
            </g>
            <g id="pathOutline" style={{"stroke": "#1592e6", "strokeWidth": 2,  "fill": "none"}}>
                <path id="pathSegments"></path>
                <path id="pathVertices"></path>
            </g>
            <g id="gridGaps">
                <rect></rect>
            </g>
            <rect id="rectSelectRegion"></rect>
            <line id="segment" className="directSelected"></line>
            <rect id="vertex" className="directSelected"></rect>
            <line id="segmentPreview" className="directSelectPreview"></line>
            <rect id="vertexPreview" className="directSelectPreview"></rect>
            <line id="hGuide" className="smartGuide" style={{strokeWidth:"2px", stroke:"#f50088", strokeDasharray: "5,5"}}></line>
            <line id="vGuide" className="smartGuide" style={{strokeWidth:"2px", stroke:"#f50088", strokeDasharray: "5,5"}}></line>
        </g>
    )
}

export default connect(mapStateToProps)(SelectionUI);
export {showAxisHandle, hideAxisHandle};